import type { ApiClient } from '@hetivo/hetivo-lib-front-utils';
import { toPaginatedResponse } from '@hetivo/hetivo-lib-front-utils';
import type {
  HetivoApiResponse,
  HetivoPaginatedResponse,
  InspectionListItem,
  InspectionStats,
  InspectionStatsCriteria,
  PaginatedResponse,
  SearchInspectionsCriteria,
} from '@hetivo/hetivo-lib-types';

// ─── Params ───────────────────────────────────────────────────────────────────

/** Search criteria plus pagination. The list endpoint takes `page`/`limit` on the query string. */
export interface SearchInspectionsParams {
  criteria: SearchInspectionsCriteria;
  /** Rows per page (the server caps it at 100). */
  limit?: number;
  /** 1-based page number. */
  page?: number;
}

// ─── API factory ──────────────────────────────────────────────────────────────

export interface InspectionApi {
  /** One page of one tab: the inspections of the caller's scope in `criteria.status`, sorted by the server. */
  search: (params: SearchInspectionsParams) => Promise<PaginatedResponse<InspectionListItem>>;
  /** The three tab counts for the scope; the menu badge reads `tabs.due`. */
  stats: (criteria: InspectionStatsCriteria) => Promise<InspectionStats>;
}

export function createInspectionApi(client: ApiClient): InspectionApi {
  return {
    search: async ({
      criteria,
      limit,
      page,
    }: SearchInspectionsParams): Promise<PaginatedResponse<InspectionListItem>> => {
      const response = await client.execute<HetivoApiResponse<HetivoPaginatedResponse<InspectionListItem>>>(() =>
        client.instance.post('/inspections/search', criteria, { params: { limit, page } }),
      );
      if (!response.data) {
        throw new Error('search: unexpected empty response');
      }
      return toPaginatedResponse(response.data);
    },

    stats: async (criteria: InspectionStatsCriteria): Promise<InspectionStats> => {
      const response = await client.execute<HetivoApiResponse<InspectionStats>>(() =>
        client.instance.post('/inspections/stats', criteria),
      );
      if (!response.data) {
        throw new Error('stats: unexpected empty response');
      }
      return response.data;
    },
  };
}
